import React, { FC, useRef, useState, useEffect } from 'react';

import {
  Button,
  Flex,
  Heading,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Stack,
  Text,
} from '@chakra-ui/react';

import useQueryParams from 'hooks/useQueryParams';
import useUrlSharing from 'hooks/useUrlSharing';

import { useMutation } from 'react-query';
import { exportItemSkusCsv, exportPackageSkusCsv } from 'api/sku';

import { IoMdListBox } from 'react-icons/io';
import { HiDocumentAdd } from 'react-icons/hi';
import {
  MdClose,
  MdFileUpload,
  MdHardware,
  MdInventory2,
  MdLocalDrink,
  MdSearch,
} from 'react-icons/md';
import { washHub } from 'store/slices';
import { CasesTable } from './CasesTable';
import { EquipmentTable } from 'components/modules/EquipmentTable';
import { ItemsTable } from './ItemsTable';
import { ManufacturerFilter } from './ManufacturerFilter';
import { PackagesTable } from './PackagesTable';
import { UserPill } from 'components/core/UserPill';
import { AddSkuModal, AddSkuModalRef } from 'components/modules/AddSkuModal';

import { Type, SkuViewContainerProps } from './Sku.props';

const SkuView: FC<SkuViewContainerProps> = (props) => {
  const [type, setType] = useState<Type>('item');
  const [active, setActive] = useState(true);
  const [search, setSearch] = useState('');
  const [manufacturer, setManufacturer] = useState<string>();
  const [urlRecord, setUrlRecord] = useState({});
  const selectedWashHubId = washHub.selectors.useSelectedWashHubId();
  const _props = {
    ...props,
    active,
    setActive,
    search,
    manufacturer,
  };

  // Only item and equipment SKUs have a manufacturer.
  const hasManufacturer = type === 'item' || type === 'equipment';

  const queryUrl = useQueryParams();
  useUrlSharing(urlRecord);

  const addSkuModalRef = useRef<AddSkuModalRef>(null);

  const csvRef = useRef<HTMLAnchorElement>(null);

  const onCreateOrder = () => {
    addSkuModalRef?.current?.onOpen?.();
  };

  const {
    mutate: exportCsv,
    data: csvData = '',
    isLoading: isExporting,
  } = useMutation(
    async () => {
      const params = { search: search || undefined, activeOnly: active };

      return type === 'package'
        ? exportPackageSkusCsv(params)
        : exportItemSkusCsv(params);
    },
    {
      onSuccess: () => {
        setTimeout(() => {
          csvRef.current?.click();
        }, 500);
      },
    },
  );

  const isType = (value: string): value is Type => {
    return ['item', 'case', 'package', 'equipment'].includes(value);
  };

  const handleSearchClear = () => {
    setSearch('');
  };

  useEffect(() => {
    const urlParams = {
      ...(type ? { type: `${type}` } : {}),
      ...(active ? { active: `${active}` } : {}),
      ...(manufacturer ? { manufacturer } : {}),
    };
    setUrlRecord(urlParams);
  }, [type, active, manufacturer]);

  useEffect(() => {
    const { type, active, manufacturer } = queryUrl;
    if (manufacturer) {
      setManufacturer(manufacturer);
    }
    if (active !== undefined) {
      setActive(active === 'true');
    }
    if (type && isType(type)) {
      setType(type);
    }
  }, []);

  return (
    <Flex direction="column" flex={1} color="support.500" overflow="auto">
      <Flex alignItems="baseline" justify="space-between">
        <Heading fontSize="24px">SKU's</Heading>
        <Flex align="flex-end">
          {type !== 'case' && type !== 'equipment' && (
            <>
              <Button
                isLoading={isExporting}
                mr={2}
                minW="200px"
                leftIcon={<MdFileUpload size="20px" />}
                onClick={() => exportCsv()}>
                Export {type === 'package' ? 'Packages' : 'Items'} to CSV
              </Button>
              {Boolean(csvData) && (
                <a
                  href={`data:text/csv;charset=utf-8,${encodeURIComponent(
                    csvData,
                  )}`}
                  download={`${
                    type === 'package' ? 'package' : 'item'
                  }-skus.csv`}
                  style={{ display: 'none' }}
                  ref={csvRef}>
                  Export CSV
                </a>
              )}
            </>
          )}
          <Button
            isDisabled={selectedWashHubId === ''}
            mr={2}
            minW="200px"
            leftIcon={<HiDocumentAdd size="20px" />}
            onClick={onCreateOrder}>
            Create SKU
          </Button>
          <UserPill />
        </Flex>
      </Flex>
      <Flex h="50px" />
      <Stack mb={6} isInline>
        <Button
          w="180px"
          borderRadius="5px"
          variant={type === 'item' ? 'solid' : 'outline'}
          size="lg"
          leftIcon={<MdLocalDrink />}
          onClick={() => setType('item')}>
          Items
        </Button>
        <Button
          w="180px"
          borderRadius="5px"
          variant={type === 'case' ? 'solid' : 'outline'}
          size="lg"
          leftIcon={<MdInventory2 />}
          onClick={() => setType('case')}>
          Cases
        </Button>
        <Button
          w="180px"
          borderRadius="5px"
          variant={type === 'package' ? 'solid' : 'outline'}
          size="lg"
          leftIcon={<IoMdListBox />}
          onClick={() => setType('package')}>
          Packages
        </Button>
        <Button
          w="180px"
          borderRadius="5px"
          variant={type === 'equipment' ? 'solid' : 'outline'}
          size="lg"
          leftIcon={<MdHardware />}
          onClick={() => setType('equipment')}>
          Equipment
        </Button>
      </Stack>

      <Flex my={6} align="flex-end">
        <Stack spacing={1} minW="240px" maxW="240px" w="100%">
          <Text color="textColor">Search SKU Name</Text>
          <InputGroup>
            <Input
              placeholder="Enter SKU name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <InputRightElement>
              <MdSearch fill="#2FB64A" />
            </InputRightElement>
          </InputGroup>
        </Stack>
        {search && (
          <IconButton
            ml={3}
            aria-label="clear-search"
            icon={<MdClose size="22px" />}
            onClick={handleSearchClear}
          />
        )}
        {hasManufacturer && (
          <Flex ml={4}>
            <ManufacturerFilter
              value={manufacturer}
              onChange={setManufacturer}
            />
          </Flex>
        )}
      </Flex>

      {type === 'case' ? (
        <CasesTable {..._props} />
      ) : type === 'package' ? (
        <PackagesTable {..._props} />
      ) : type === 'equipment' ? (
        <EquipmentTable {..._props} />
      ) : (
        <ItemsTable {..._props} />
      )}
      <AddSkuModal ref={addSkuModalRef} type={type} />
    </Flex>
  );
};

export default SkuView;
